import { Ability, AbilityBuilder } from '@casl/ability';
import { type ValueOf } from '@/types';
import { Constants } from '@/constants';
import { ContactType, type User, UserType } from '@/generated/graphql';
import { type UntypedLibrary } from '@/types/utils';

const { Type, Module, Permission, PermissionAction, Feature } = Constants;

export type Features = ValueOf<typeof Feature>;
export type Modules = ValueOf<typeof Module>['name'];
type Permissions = ValueOf<typeof Permission>;

export type FeaturesAndPermissions = Features | Modules | Permissions;

/**
 * Define abilities granurally so that the actions that can be performed on
 * specific subjects are checked explicitly with TypeScript.
 *
 * For example, if the "readIncomeAndCost" action can only be performed on
 * "Project", then TypeScript can already tell you this information with
 * autocompletion + type checks.
 */

export type AppAbilities =
	| [typeof PermissionAction.READ, typeof Type.Notification]
	| [typeof PermissionAction.CREATE, typeof Type.CalendarEvent]
	| [typeof PermissionAction.CREATE, typeof Type.Task]
	| [typeof PermissionAction.MODIFY, typeof Type.Task]
	| [
			typeof PermissionAction.CREATE | typeof PermissionAction.READ,
			typeof ContactType.Person,
	  ]
	| [typeof PermissionAction.READ, typeof Type.TimeEntry]
	| [typeof PermissionAction.CREATE, typeof ContactType.Company]
	| [typeof PermissionAction.CREATE, typeof Type.Project]
	| [typeof PermissionAction.CREATE, typeof Type.Quote]
	| [typeof PermissionAction.CREATE, typeof Type.Order]
	| [typeof PermissionAction.CREATE, typeof Type.Invoice]
	| [typeof PermissionAction.CREATE, typeof Type.Bill]
	| [typeof PermissionAction.CREATE, typeof Type.PurchaseOrder]
	| [typeof PermissionAction.CREATE, typeof Type.RealEstateObject]
	| [typeof PermissionAction.CREATE, typeof Type.Expense]
	| [typeof PermissionAction.READ, typeof Type.SearchResult]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_SEE_MARGIN_AND_MARKUP,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_SEE_OTHER_LABORCOST,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_SEE_PROJECT_INCOME_COST,
	  ]
	| [
			typeof PermissionAction.MODIFY,
			typeof Permission.CAN_SET_GENERAL_SETTINGS,
	  ]
	| [typeof PermissionAction.HAS_PERMISSION, typeof Type.Setting]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_SEE_PROJECT_RESOURCING,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_ADD_AND_MODIFY_TIMEOFF,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_CHANGE_PROJECT_STATUS_TO_COMPLETED,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_CONFIRM_QUOTES,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_SEE_QUOTE_BUDGETED_HOURS_ON_TASKS,
	  ]
	| [
			typeof PermissionAction.HAS_PERMISSION,
			typeof Permission.CAN_LINK_CUSTOMER_PORTAL_USER_TO_PROJECT,
	  ];

/**
 * The CASL Ability configured with this app's abilities.
 */
export type AppAbility = Ability;

/**
 * Configure CASL abilities based on the user's enabled features and
 * permissions.
 *
 * Accepts a combination of all enabled modules, features, and permissions as
 * one list. Also requires the currently logged in user.
 */
export const defineAbilitiesFor = (
	featuresAndPermissions: FeaturesAndPermissions[],
	user: Pick<User, 'id' | 'type'>
) => {
	const {
		can: allow,
		cannot: deny,
		build,
	} = new AbilityBuilder<AppAbility>(Ability);

	const has = (featureOrPermission: FeaturesAndPermissions) =>
		featuresAndPermissions.includes(featureOrPermission);

	const isCustomerPortalUser = user.type === UserType.Cupo;
	const isSuperAdmin = has(Permission.IS_SUPER_ADMIN);

	// Use the `allow` function to define if the user has access to the entites

	// When adding a new permission check, make sure to update the tests for
	// `defineAbilitiesFor`

	if (has(Feature.NOTIFICATIONS)) {
		allow(PermissionAction.READ, Type.Notification);
	}

	if (has(Module.CALENDAR.name)) {
		allow(PermissionAction.CREATE, Type.CalendarEvent);
	}

	if (has(Module.TASKS.name)) {
		allow(PermissionAction.CREATE, Type.Task);
		allow(PermissionAction.MODIFY, Type.Task);
	}

	if (has(Module.PERSONS.name)) {
		allow(PermissionAction.READ, ContactType.Person);
	}

	if (has(Module.PERSONS.name) && has(Permission.CAN_CREATE_PERSON)) {
		allow(PermissionAction.CREATE, ContactType.Person);
	}

	if (has(Module.COMPANIES.name) && has(Permission.CAN_CREATE_COMPANY)) {
		allow(PermissionAction.CREATE, ContactType.Company);
	}

	if (has(Module.PROJECTS.name) && has(Permission.CAN_CREATE_PROJECTS)) {
		allow(PermissionAction.CREATE, Type.Project);
	}

	if (has(Module.REALESTATE.name) && has(Permission.CAN_ADD_RE_OBJECTS)) {
		allow(PermissionAction.CREATE, Type.RealEstateObject);
	}

	if (has(Module.QUOTES.name)) {
		allow(PermissionAction.CREATE, Type.Quote);
	}

	if (has(Module.ORDERS.name)) {
		allow(PermissionAction.CREATE, Type.Order);
	}

	if (has(Module.INVOICES.name) && has(Permission.CAN_CREATE_INVOICE)) {
		allow(PermissionAction.CREATE, Type.Invoice);
	}

	if (has(Module.BILLS.name) && has(Permission.CAN_CREATE_BILLS)) {
		allow(PermissionAction.CREATE, Type.Bill);
	}

	if (
		has(Module.PURCHASE_ORDERS.name) &&
		has(Permission.CAN_CREATE_PURCHASE_ORDERS)
	) {
		allow(PermissionAction.CREATE, Type.PurchaseOrder);
	}

	if (has(Module.EXPENSES.name)) {
		allow(PermissionAction.CREATE, Type.Expense);
	}

	if (has(Feature.TIME_TRACKER)) {
		allow(PermissionAction.READ, Type.TimeEntry);
	}

	if (has(Permission.CAN_SEE_OTHER_LABORCOST)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_OTHER_LABORCOST
		);
	}

	if (has(Permission.CAN_SEE_MARGIN_AND_MARKUP)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_MARGIN_AND_MARKUP
		);
	}

	if (has(Permission.CAN_SEE_PROJECT_INCOME_COST)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_PROJECT_INCOME_COST
		);
	}

	if (has(Permission.CAN_SET_GENERAL_SETTINGS)) {
		allow(PermissionAction.MODIFY, Permission.CAN_SET_GENERAL_SETTINGS);
	}

	if (has(Module.SETTINGS.name)) {
		allow(PermissionAction.HAS_PERMISSION, Type.Setting);
	}

	if (has(Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS
		);
	}

	if (has(Permission.CAN_SEE_PROJECT_RESOURCING)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_PROJECT_RESOURCING
		);
	}

	if (has(Permission.CAN_ADD_AND_MODIFY_TIMEOFF)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_ADD_AND_MODIFY_TIMEOFF
		);
	}

	if (has(Permission.CAN_CHANGE_PROJECT_STATUS_TO_COMPLETED)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_CHANGE_PROJECT_STATUS_TO_COMPLETED
		);
	}

	if (has(Permission.CAN_CONFIRM_QUOTES)) {
		allow(PermissionAction.HAS_PERMISSION, Permission.CAN_CONFIRM_QUOTES);
	}

	if (has(Permission.CAN_SEE_QUOTE_BUDGETED_HOURS_ON_TASKS)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_QUOTE_BUDGETED_HOURS_ON_TASKS
		);
	}

	if (has(Permission.CAN_EXPORT_DATA)) {
		allow(PermissionAction.HAS_PERMISSION, Permission.CAN_EXPORT_DATA);
	}

	if (has(Permission.CAN_LINK_CUSTOMER_PORTAL_USER_TO_PROJECT)) {
		allow(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_LINK_CUSTOMER_PORTAL_USER_TO_PROJECT
		);
	}

	// Customer Portal users have a few special permission needs

	if (isCustomerPortalUser) {
		deny(PermissionAction.READ, Type.TimeEntry);
	}

	if (!isCustomerPortalUser) {
		allow(PermissionAction.READ, Type.SearchResult);
	}

	if (isSuperAdmin) {
		allow(PermissionAction.HAS_PERMISSION, Permission.IS_SUPER_ADMIN);
	}

	return build({
		// obj is typed as `never` in `casl`...
		detectSubjectType: (obj: UntypedLibrary) => obj.__typename,
	});
};
